import { IPromise } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { IStateInit } from "@gtmhub/core/routing";
import { CfMap } from "@gtmhub/customFields";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IGoal } from "@gtmhub/goals/models";
import { IInsightSelectorData, InsightService } from "@gtmhub/insights";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { OkrEvents } from "@gtmhub/okrs";
import { MetricDeadLine, disableDatesOutsideSessionTimeframe, metricFormStrings, setMetricDueDates } from "@gtmhub/okrs/metrics/utils";
import { IPlanningSession, ISessionsStoreState, IUserItemAllowedActions, PlanningSessionsActions } from "@gtmhub/sessions";
import { IUnitFormattingForm } from "@gtmhub/shared/controllers/unit-formatting";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { AutoFieldsService } from "@gtmhub/shared/services/auto-fields";
import { INgRedux } from "@gtmhub/state-management/models";
import { getCurrentUserId } from "@gtmhub/users";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { MetricTargetOperator } from "@webapp/okrs/metrics/models/metric.models";
import { IMetric, MetricFormEventType, MetricService } from "..";
import { ICheckin } from "../models/models";
import { IMetricMethodologySettingsUI } from "./../../../goals/components/facades/okr-methodology.facade";

export interface IEditDynamicMetricParams {
  metricId: string;
  metric: IMetric;
  id: string;
}

interface IEditDynamicMetricReduxScope {
  sessionIdMap: Record<string, IPlanningSession>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: ISessionsStoreState): IEditDynamicMetricReduxScope => {
  return {
    sessionIdMap: state.sessions.map,
    reduxItemsFetched: state.sessions.isFetched,
    reduxItemsError: state.sessions.error,
  };
};

export interface IEditDynamicMetricScope extends IModalScope, IEditDynamicMetricReduxScope, IExtendedRootScopeService {
  session: IPlanningSession;
  indicators: {
    loading: boolean;
    saving: boolean;
  };
  metric: IMetric;
  params: IEditDynamicMetricParams;
  allowedActions: string[];

  dueDatePicker: {
    minDate;
    maxDate;
  };
  dates: {
    dueDate: string;
    softDueDate: string;
  };

  softDueDatePicker: {
    minDate;
    maxDate;
  };
  // use to determine what to do with the variables on save
  // possible values are: "none", "hardDeadline", "hardAndSoftDeadline"
  useDeadline: MetricDeadLine;
  goal: IGoal;
  isLoaded: boolean;
  errorMessage: string;
  insightSelector: {
    data: IInsightSelectorData;
    readOnly: boolean;
  };
  requiredCfNotEmpty: boolean;
  methodologySettings: IMetricMethodologySettingsUI;
  onInsightSelectorChange(): void;
  save(): void;
  includeCritical(useCritical: boolean): boolean;
  validateInputValues(targetOperator: string, targetValue: number, criticalValue: number, initialValue: number): boolean;
  insertCustomFormat(): void;
  updateCustomFields(customFields: CfMap): void;
  setRequiredCf(requiredCf: { areFilled: boolean }): void;
  selectMultipleOwners(ids: string[]): void;
  isFieldPartOfWorkflow(filedName: string): boolean;
  searchInsightSuggestions(name: string): void;
  setDeadline(deadline: MetricDeadLine): void;
}

export class EditDynamicMetricCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "GoalService",
    "MetricService",
    "PermissionsService",
    "UIErrorHandlingService",
    "UnitFormattingForm",
    "$ngRedux",
    "PlanningSessionsActions",
    "InsightService",
    "FeatureToggleService",
    "OkrMethodologyFacade",
    "AutoFieldsService",
    "AnalyticsService",
  ];

  private okrEvents: OkrEvents;
  private isNumberFormattingFeatureEnabled: boolean;

  constructor(
    private $rootScope: IGtmhubRootScopeService,
    private $scope: IEditDynamicMetricScope,
    private $state: StateService,
    private $stateParams: IEditDynamicMetricParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private permissionsService: PermissionsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private unitFormattingForm: IUnitFormattingForm,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private insightService: InsightService,
    private featureToggleService: FeatureToggleService,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private autoFieldsService: AutoFieldsService,
    private analyticsService: AnalyticsService
  ) {
    this.okrEvents = new OkrEvents(this.$scope);

    this.$scope.params = { ...this.$stateParams };
    this.$scope.isLoaded = true;

    this.$scope.indicators = {
      loading: false,
      saving: false,
    };

    this.$scope.dates = {
      dueDate: null,
      softDueDate: null,
    };
    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));
    this.$scope.save = this.save;
    this.$scope.includeCritical = this.includeCritical;
    this.$scope.validateInputValues = this.validateInputValues;
    this.$scope.insertCustomFormat = this.insertCustomFormat;
    this.$scope.onInsightSelectorChange = this.onInsightSelectorChange;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.setRequiredCf = this.setRequiredCf;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);
    this.$scope.searchInsightSuggestions = this.searchInsightSuggestions;
    this.$scope.setDeadline = this.setDeadline;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private searchInsightSuggestions = (name: string) => {
    this.$rootScope.$broadcast("searchInsightSuggestions", { name });
  };

  private init = (): IPromise<unknown> => {
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$scope.indicators.loading = true;

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(
        () => {
          return this.featureToggleService.isFeatureAvailable(FeatureFlag.NumberFormatting).then((isNumberFormattingEnabled) => {
            this.isNumberFormattingFeatureEnabled = isNumberFormattingEnabled;
            return this.goalService.getGoal(this.$scope.params.id).then(
              (goal) => {
                this.$scope.goal = goal;
                this.setMetricMethodology();

                if (this.$scope.params.metricId) {
                  this.$scope.metric = this.$scope.goal.metrics.find((metric) => metric.id === this.$scope.params.metricId);
                } else {
                  this.$scope.metric = angular.copy(this.$scope.params.metric);
                }

                // in a case when user refresh while creating a dynamic KR
                if (!this.$scope.metric) {
                  this.$scope.$dismiss();
                }

                const targetId = this.$scope.metric.id ? this.$scope.metric.id : this.$scope.goal.id;
                const targetType = this.$scope.metric.id ? "metric" : "goal";

                this.permissionsService
                  .getCurrentUserAllowedActionsPerItem(targetId, targetType)
                  .then(
                    (actions: IUserItemAllowedActions) => {
                      this.$scope.indicators.loading = false;
                      this.$scope.allowedActions = actions.currentUserActions;

                      if (this.$scope.goal.sessionId && this.$scope.goal.sessionId !== "") {
                        this.$scope.session = this.$scope.sessionIdMap[this.$scope.goal.sessionId];
                        disableDatesOutsideSessionTimeframe(this.$scope, this.$stateParams);
                      }

                      this.$scope.insightSelector = {
                        data: this.metricService.generateInsightSelectorData(this.$scope.metric),
                        readOnly: !(this.$rootScope.hasPermission(metricFormStrings.manageData) || this.$rootScope.hasRole(metricFormStrings.admin)),
                      };

                      this.$scope.$broadcast("focusTextInput");
                    },
                    (err) => this.handleIndicatorError(err, "loading")
                  )
                  .then(() => {
                    if (isNumberFormattingEnabled && this.$scope.metric.insightName && !this.$scope.metric.id) {
                      return this.insightService.getInsightV2(this.$scope.metric.insightName).then((insight) => {
                        const unit = insight.formatting.unit;
                        this.$scope.metric.format.prefix = insight.formatting.suffix ? "" : unit;
                        this.$scope.metric.format.suffix = insight.formatting.suffix ? unit : "";
                      });
                    }
                  });

                this.metricService.trackMetricForm(MetricFormEventType.KR_FORM_OPENED, this.$scope.metric);
              },
              (err) => this.handleIndicatorError(err, "loading")
            );
          });
        },
        () => this.uiErrorHandlingService.handleModal(this.$scope.reduxItemsError)
      );
  };

  private setMetricMethodology(): void {
    this.$scope.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.$scope.session);
  }

  private setDeadline = (deadline: MetricDeadLine): void => {
    this.$scope.useDeadline = deadline;
  };

  private isFieldPartOfWorkflow(fieldName: string): boolean {
    return this.metricService.isFieldPartOfWorkflow(fieldName, this.$scope.session);
  }

  private save = () => {
    this.$scope.indicators.saving = true;

    setMetricDueDates(this.$scope);

    // set metric.critical to null if not dealing with anti-target KRs
    this.setMetricCriticalTarget(this.$scope.metric);

    if (this.$scope.params.metricId) {
      this.updateDynamicMetric();
    } else {
      this.createDynamicMetric();
    }
  };

  private createDynamicMetric = (): void => {
    const shouldCheckin = this.$scope.metric.actual !== this.$scope.metric.initialValue;
    this.metricService.createMetric(this.$scope.metric).then(
      (newMetric: IMetric) => {
        if (shouldCheckin) {
          const checkin: ICheckin = {
            checkInDate: new Date().toISOString(),
            actual: this.$scope.metric.actual,
            confidence: this.$scope.metric.confidence?.value,
            changedBy: getCurrentUserId(),
            customFields: this.$scope.metric.customFields,
          };
          this.metricService.checkin(newMetric, checkin).then(() => {
            this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
              checkin,
              metric: this.$scope.metric,
              goal: this.$scope.goal,
              session: this.$scope.session,
            });
          });
        }

        this.okrEvents.broadcastGoalsChanged("goalChanged");

        this.$scope.indicators.saving = false;

        this.$state.go("^.^");

        if (this.$scope.insightSelector?.data.insightTemplate) {
          const payload = this.autoFieldsService.getRecommendedInsightAnalyticsData(this.$scope.insightSelector.data);
          this.autoFieldsService.sendInsightRecommendtationFeedback(payload);
        }

        if (this.$scope.insightSelector?.data) {
          this.analyticsService.track("Key Result Insight Suggestion Created", {
            totalInsightsSuggested: this.$scope.insightSelector.data.suggestedInsights?.length || 0,
            ...(this.$scope.insightSelector.data.insightTemplate && {
              insightSuggestedId: this.$scope.insightSelector.data.insightTemplate.id,
            }),
            isAutomationUsed: !!this.$scope.insightSelector.data.insightTemplate,
          });
        }
      },
      (error) => this.handleIndicatorError(error, "saving", true)
    );
  };

  private updateDynamicMetric = (): void => {
    if (this.$scope.goal.locked && this.$scope.session.workflow?.editableKrFields?.length > 0) {
      const partialMetric: Partial<IMetric> = {
        id: this.$scope.metric.id,
      };

      const properties = this.metricService.filterWorkflowSelectionProperties(this.$scope.session.workflow.editableKrFields);

      properties.forEach((property) => {
        partialMetric[property] = this.$scope.metric[property];
      });

      this.patchMetric(partialMetric);
    } else {
      this.patchMetric(this.$scope.metric);
    }

    /** if user has selected a recommended insight by template */
    if (this.$scope.insightSelector.data.insightTemplate) {
      const payload = this.autoFieldsService.getRecommendedInsightAnalyticsData(this.$scope.insightSelector.data);
      this.autoFieldsService.sendInsightRecommendtationFeedback(payload);
    }
  };

  private patchMetric(metric: Partial<IMetric>): void {
    this.metricService.patchMetric(metric).then(
      () => this.handleDynamicMEtricUpdated(),
      (error) => this.handleIndicatorError(error, "saving", true)
    );
  }

  private handleDynamicMEtricUpdated() {
    this.okrEvents.broadcastGoalsChanged("goalChanged");
    this.$scope.indicators.saving = false;
    this.$scope.$dismiss();
  }

  private setMetricCriticalTarget = (metric: IMetric): void => {
    if (metric.targetOperator === "should_stay_above" || metric.targetOperator === "should_stay_below") {
      return;
    }

    // if metric is not anti-target KR --> set critical value to null
    this.$scope.metric.critical = null;
  };

  private validateInputValues = (targetOperator: MetricTargetOperator, targetValue: number, criticalValue: number, initialValue: number): boolean => {
    if (initialValue === null || targetValue === null) {
      this.$scope.errorMessage = null;

      return false;
    }

    if (targetOperator === "should_stay_above" && criticalValue !== null && typeof criticalValue !== "undefined" && criticalValue >= targetValue) {
      this.$scope.errorMessage = "critical_lower_than_target";

      return false;
    }

    if (targetOperator === "should_stay_below" && criticalValue !== null && typeof criticalValue !== "undefined" && criticalValue <= targetValue) {
      this.$scope.errorMessage = "critical_greater_than_target";

      return false;
    }

    if (targetOperator === "at_least" && initialValue >= targetValue) {
      this.$scope.errorMessage = "initial_lower_than_target";

      return false;
    }

    if (targetOperator === "at_most" && initialValue <= targetValue) {
      this.$scope.errorMessage = "initial_greater_than_target";

      return false;
    }

    return true;
  };

  private includeCritical = (useCritical: boolean): boolean => {
    if (useCritical === true) {
      // setting metric.critical to be null when critical target is not used
      this.$scope.metric.critical = null;

      return false;
    } else {
      // ng-init in this case is set by the attributes of the input field
      return true;
    }
  };

  private insertCustomFormat = (): void => {
    this.unitFormattingForm.open({ formatting: this.$scope.metric.format });
  };

  private onInsightSelectorChange = (): void => {
    this.metricService.updateMetricInsightData(this.$scope.metric, this.$scope.insightSelector.data, this.isNumberFormattingFeatureEnabled);
  };

  private updateCustomFields = (customFields: CfMap): void => {
    this.$scope.metric.customFields = customFields;
  };

  private setRequiredCf = (requiredCf: { areFilled: boolean }): void => {
    this.$scope.requiredCfNotEmpty = requiredCf.areFilled;
  };

  private handleIndicatorError = <E>(err: E, indicator: keyof IEditDynamicMetricScope["indicators"], inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.metric.ownerIds = ids;
  };
}
